/**
 * TaskGroup Component
 *
 * Feature: 650-task-grouping
 *
 * Container component that wraps TaskGroupHeader and TaskGroupChildren.
 * Handles expand/collapse state and renders the complete group UI.
 * Hero image spans full height of collapsed state on the left.
 */

import type { TaskGroupProps } from '@/types/task-groups';
import { TaskGroupHeader } from './TaskGroupHeader';
import { TaskGroupChildren } from './TaskGroupChildren';
import { TaskGroupActions } from './TaskGroupActions';
import { TaskGroupHeroImage } from './TaskGroupHeroImage';
import { useTaskGroupHero } from './useTaskGroupHero';
import logger from '@/utils/logger';

export function TaskGroup({
  group,
  isExpanded,
  onToggle,
  onCompleteAll,
  onDismissAll,
  onNavigateToTask,
  isFocused = false,
}: TaskGroupProps) {
  const pendingIds = group.tasks
    .filter(t => t.status === 'pending')
    .map(t => t.id);

  // Fetch hero image data at group level for full-height rendering
  const { heroPhoto, sessionTitle, isLoading: heroLoading } = useTaskGroupHero(group);

  const handleNavigate = () => {
    logger.info('Tasks', 'Navigating to group critique', {
      groupKey: group.key,
      url: group.url,
    });
  };

  const handleCompleteAll = () => {
    logger.info('Tasks', 'Completing all tasks in group', {
      groupKey: group.key,
      count: pendingIds.length,
    });
    onCompleteAll(pendingIds);
  };

  const handleDismissAll = () => {
    logger.info('Tasks', 'Dismissing all tasks in group', {
      groupKey: group.key,
      count: pendingIds.length,
    });
    onDismissAll(pendingIds);
  };

  return (
    <div
      className={`rounded-lg border bg-surface-1 transition-all overflow-hidden ${
        isFocused
          ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface-1 border-primary'
          : 'border-border hover:border-primary/50'
      }`}
      data-group-key={group.key}
    >
      {/* Collapsed State: Hero image on left, content on right */}
      <div className="flex">
        {/* Hero Image - full height, 3:2 aspect ratio width */}
        <TaskGroupHeroImage
          heroPhoto={heroPhoto}
          sessionTitle={sessionTitle}
          isLoading={heroLoading}
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="p-4">
            <TaskGroupHeader
              group={group}
              isExpanded={isExpanded}
              onToggle={onToggle}
              onNavigate={handleNavigate}
              sessionTitle={sessionTitle}
            />
          </div>

          {/* Actions (shown when there are pending tasks) */}
          {group.pendingCount > 0 && (
            <div className="px-4 pb-3 flex items-center justify-end gap-2">
              <TaskGroupActions
                group={group}
                onCompleteAll={handleCompleteAll}
                onDismissAll={handleDismissAll}
              />
            </div>
          )}
        </div>
      </div>

      {/* Expanded Children */}
      {isExpanded && (
        <div className="border-t border-border">
          <TaskGroupChildren group={group} onNavigateToTask={onNavigateToTask} />
        </div>
      )}
    </div>
  );
}
